import { Box } from "@chakra-ui/react";
import React from "react";
import Balancer from "react-wrap-balancer";
import {
  AlertIcon,
  CalendarIcon,
  CornerDownRightIcon,
  GlobeIcon,
  HeartIcon,
  LayoutIcon,
  LightningIcon,
  MinusCircleIcon,
  MoneyIcon,
  PercentIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SettingsIcon,
  SparklesIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/utilities-energy.png";
import { dataBreachStat, personalizedExperiencesStat, type CdpIndustryConfig } from "./shared";

export const utilitiesEnergyCdpConfig: CdpIndustryConfig = {
  icon: LightningIcon,
  name: "utilities & energy",
  titleCaseName: "Utilities & Energy",
  pluralNoun: "utilities & energy companies",
  color: "violet",
  seoCopy:
    "Leverage all your utilties & energy data, including customers, accounts, billing cycles, usage records, grid performance logs, and payment records.",
  heroImage: {
    src: hero,
    alt: "A photograph of solar panels with a list of example data tables overlayed. The data tables included are for billing cycles, grid performance, accounts, usage records, and payments.",
  },
  adoptingCdpsDescription:
    "Utilities and energy providers face increasing pressure to meet evolving customer expectations, particularly around transparency. While most customers have limited alternatives, the real challenge is maintaining trust and satisfaction. ",
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "green",
      title: "Increase program enrollment",
    },
    {
      icon: PersonWithCheckIcon,
      color: "blue",
      title: "Improve customer satisfaction",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStatsTitleMaxW: "3xl",
  dataStats: [
    {
      title: "$1.1T",
      description: (
        <Balancer>
          estimated market size <Box as="br" display={{ base: "block", lg: "none" }} />
          for the utilities industry
        </Balancer>
      ),
      href: "https://www.ibisworld.com/industry-statistics/market-size/utilities-united-states/",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "Exxon Mobile delivers personalized offers and savings to rewards members to help them save millions",
      href: "https://rewards.exxon.com/welcome/home",
    },
    {
      title: "Excel Energy provides more transparency to customers around energy consumption and power outages",
      href: "https://www.ey.com/en_gl/insights/consulting/an-energy-companys-transformation-for-the-21st-century-customer",
    },
    {
      title: "PG&E is investing in custom journeys and personalized recommendations for households",
      href: "https://www.pge.com/assets/pge/docs/save-energy-and-money/energy-savings-programs/EE-8yrBizPlan-2024-2031.pdf",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new customer sign-ups for utility or renewable services by using location and usage data to target households moving into new areas or considering a provider switch.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing current customers from acquisition campaigns using CRM data, reallocating budget toward untapped high-value prospects.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who explored renewable energy options but didn’t convert by serving dynamic ads highlighting cost savings and environmental benefits, increasing conversions.",
      },
      // Second row
      {
        icon: SunshineIcon,
        title: "Seasonal promotions",
        description:
          "Drive adoption during high-demand periods by highlighting home efficiency upgrades or renewable energy benefits, increasing sign-ups for energy plans.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize customer insights by offering anonymized energy consumption audience segments to partners, such as smart home device manufacturers, driving incremental revenue.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend smart home energy solutions, home efficiency upgrades, or solar panel installations to increase revenue per household.",
      },
      {
        icon: CalendarIcon,
        title: "Seasonal energy savings campaigns",
        description:
          "Provide tips and promotions on energy-saving products during peak seasons to improve satisfaction and reduce energy strain.",
      },
      {
        icon: SunshineIcon,
        title: "Renewable adoption campaigns",
        description:
          "Encourage customers to transition to renewable energy plans with educational content and exclusive incentives.",
      },
      // Second row
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Offer special discounts or bill credits to win back former customers who switched providers.",
      },
      {
        icon: HeartIcon,
        title: "Loyalty rewards campaigns",
        description:
          "Offer bill credits, tiered discounts, or VIP customer incentives to reward long-term service users.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Target specific regions with infrastructure upgrades, special energy programs, or local rebates to drive adoption.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LayoutIcon,
        title: "Dynamic home page content",
        description:
          "Display tailored energy-saving tips, usage insights, or plan upgrades based on the user’s energy profile to increase engagement and satisfaction.",
      },
      {
        icon: PercentIcon,
        title: "Energy usage tracking",
        description:
          "Provide real-time energy monitoring updates via personalized dashboards to boost app engagement and encourage efficient energy consumption.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Recommend impactful actions such as switching to renewable energy, enrolling in autopay, or upgrading to energy storage to drive engagement and revenue.",
      },
      // Second row
      {
        icon: AlertIcon,
        title: "Proactive bill reminders",
        description:
          "Notify users about upcoming payments, usage overages, or expiring discounts to reduce billing-related churn and improve satisfaction.",
      },
      {
        icon: TrendIcon,
        title: "Efficiency goal tracking",
        description:
          "Set monthly energy-saving goals and reward customers for achieving them, promoting satisfaction and eco-friendly behaviors.",
      },
      {
        icon: SparklesIcon,
        title: "Gamified sustainability rewards",
        description:
          "Incentivize energy-saving habits by offering badges, points, or discounts for milestones like reducing usage or enrolling in renewable plans.",
      },
      // Third row
      {
        icon: PinIcon,
        title: "Localized service updates",
        description:
          "Provide location-based notifications about outages, infrastructure upgrades, or new renewable energy options to increase transparency and build trust.",
      },
      {
        icon: LightningIcon,
        title: "Energy storage optimization tips",
        description:
          "Offer personalized recommendations for optimizing home battery storage usage to improve efficiency and adoption of advanced energy solutions.",
      },
      {
        icon: PersonWithCheckIcon,
        title: "Personalized recommendations",
        description:
          "Suggest tailored renewable energy plans, smart home integrations, or efficiency upgrades based on user consumption patterns and environmental preferences to drive adoption and long-term engagement.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional:
        "Uses predefined models that may not fully capture energy usage patterns or renewable adoption trends",
      composable:
        "Supports tailored models to handle energy consumption, smart meter data, and renewable energy preferences",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying eco-conscious households or high-usage customers",
      composable:
        "Enables granular segmentation using data like peak energy usage patterns and green energy program participation",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like energy efficiency tips or billing reminders",
      composable:
        "Powers fully adaptable journeys for events like renewable plan adoption, smart thermostat promotions, and outage alerts",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across smart meters utility accounts and mobile apps",
      composable:
        "Supports custom algorithms to unify profiles across household data, energy devices, and billing platforms",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Eco-conscious consumers exploring upgrades",
    firstCategoryLabel: "Smart meter interest (no sign-up)",
    secondCategoryLabel: "Solar consultation requests",
  },
};
